import type { Agent } from './types'
import { area, currentlyCapped, eliteRecent, mlsPrimary } from './predicates'
import { formatDate, fullName, monthDay } from './format'
import { labelLines, learnCities, parseAddress, tidyCase } from './address'
import { buildLabelsDocx, buildXlsx, PER_PAGE, type Sheet } from './officefiles'

/**
 * Taking a selection off the screen and into a file.
 *
 * Two outputs, one selection. Whatever is on the agent list — the tab, the
 * search box, every dropdown — is what comes out, because both of these are
 * handed the rows the list is already showing rather than going back to the
 * database with their own idea of the question.
 *
 * The address a label uses is the mailing address, falling back to the home
 * address where there is no mailing one. Chris chose that on 2026-08-14: it is
 * 730 of 785 agents rather than 665, and for 515 of them the two are the same
 * address anyway. The sheet says which one was used, so a letter that goes to
 * someone's house rather than their office is a visible decision.
 */

export type Selected = {
  /** Everyone in the current selection. */
  agents: Agent[]
  /** Those with an address of some kind — the ones labels can be made for. */
  mailable: Agent[]
  /** Those without. The list Renee has to chase. */
  unmailable: Agent[]
}

export const postalAddress = (a: Agent): string | null =>
  (a.mailing_address && a.mailing_address.trim()) ||
  (a.home_address && a.home_address.trim()) ||
  null

export function split(agents: Agent[]): Selected {
  const mailable: Agent[] = []
  const unmailable: Agent[] = []
  for (const a of agents) (postalAddress(a) ? mailable : unmailable).push(a)
  return { agents, mailable, unmailable }
}

/** Pages of Avery 5160 a run of labels will use. */
export const sheetsNeeded = (n: number) => Math.ceil(n / PER_PAGE)

const yesNo = (v: boolean) => (v ? 'Yes' : '')

const AGENT_HEADERS = [
  'First name', 'Last name', 'Email', 'Real email', 'Phone',
  'Street', 'Town', 'State', 'ZIP', 'Address used', 'Check address', 'Address as stored',
  'Team', 'Team role', 'Area', 'MLS',
  'Licence number', 'Licence expires',
  'Birthday', 'Work anniversary',
  'Capped', 'Elite', 'Team leader', 'Status',
]

const MISSING_HEADERS = ['First name', 'Last name', 'Email', 'Phone', 'Team', 'Area', 'Status']

/**
 * `cities` is learned from the WHOLE roster, not from the selection — a town
 * seen once elsewhere is what lets a one-agent export split correctly.
 */
export function buildSheets(selected: Selected, roster: Agent[]): Sheet[] {
  const cities = learnCities(roster.flatMap((a) => [a.mailing_address, a.home_address]))

  const rows = selected.agents.map((a) => {
    const raw = postalAddress(a)
    const p = raw ? parseAddress(raw, cities) : null
    return [
      a.first_name ?? '',
      a.last_name ?? '',
      a.email ?? '',
      a.join_real_email ?? '',
      a.phone ?? '',
      p?.street ?? '',
      p?.city ?? '',
      p?.state ?? '',
      p?.zip ?? '',
      raw ? (a.mailing_address && a.mailing_address.trim() ? 'Mailing' : 'Home') : '',
      p?.suspect ? 'Check' : '',
      raw ?? '',
      a.team_name ?? '',
      a.team_role ?? '',
      area(a),
      mlsPrimary(a),
      a.license_number ?? '',
      formatDate(a.license_expires) ?? '',
      monthDay(a.birthday_month, a.birthday_day) ?? '',
      monthDay(a.anniversary_month, a.anniversary_day) ?? '',
      yesNo(currentlyCapped(a)),
      yesNo(eliteRecent(a)),
      yesNo(a.is_team_leader),
      a.is_active ? 'Active' : 'Departed',
    ]
  })

  const missing = selected.unmailable.map((a) => [
    a.first_name ?? '',
    a.last_name ?? '',
    a.email ?? '',
    a.phone ?? '',
    a.team_name ?? '',
    area(a),
    a.is_active ? 'Active' : 'Departed',
  ])

  const sheets: Sheet[] = [{ name: 'Agents', headers: AGENT_HEADERS, rows }]
  // Only when there are any. An empty sheet headed "No address" reads as a
  // problem rather than as the absence of one.
  if (missing.length) {
    sheets.push({ name: 'No address', headers: MISSING_HEADERS, rows: missing })
  }
  return sheets
}

export function buildWorkbook(selected: Selected, roster: Agent[]): Uint8Array {
  return buildXlsx(buildSheets(selected, roster))
}

/**
 * The label lines, in the order they will be printed.
 *
 * Separate from `buildLabels` so the count on screen and in the filename comes
 * from the labels that actually exist rather than from the number of people
 * who have an address. They should be the same number; if they ever are not,
 * the honest one is this.
 */
export function labelsFor(
  selected: Selected,
  roster: Agent[],
  { perHousehold = false }: { perHousehold?: boolean } = {},
): string[][] {
  const cities = learnCities(roster.flatMap((a) => [a.mailing_address, a.home_address]))

  /**
   * Fourteen addresses in this roster belong to two or more agents — couples
   * and family teams. Left alone that is two envelopes through one letterbox.
   *
   * Grouped, the names share a label: "Ben and Sarah Jaeger". Not the default,
   * because a licence renewal is addressed to a person and only that person
   * should open it. Renee chooses per run.
   */
  if (perHousehold) {
    const out: string[][] = []
    for (const group of householdGroups(selected)) {
      const parsed = parseAddress(group.address, cities)
      if (!parsed) continue
      // Each name tidied BEFORE joining. tidyCase judges the whole string, and
      // the lower-case "and" in a joined pair makes two shouting names look
      // like a properly-typed one — so they print as stored.
      const lines = labelLines(joinNames(group.names.map(tidyCase)), parsed)
      if (lines.length > 1) out.push(lines)
    }
    return out
  }

  return selected.mailable
    .map((a) => {
      const p = parseAddress(postalAddress(a), cities)
      return p ? labelLines(fullName(a), p) : []
    })
    .filter((l) => l.length > 1)
}

/** "Ben and Sarah Jaeger", "A, B and C" — a line, not a list. */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The selection grouped by address — one entry per letterbox.
 *
 * Exported so the count on screen, the labels, and the tests all come from one
 * grouping rather than three that could disagree about what counts as the same
 * address.
 */
export function householdGroups(selected: Selected): { address: string; names: string[] }[] {
  const byAddress = new Map<string, { address: string; names: string[] }>()
  for (const a of selected.mailable) {
    const raw = postalAddress(a)
    if (!raw) continue
    const key = raw.replace(/\s+/g, ' ').trim().toLowerCase()
    const entry = byAddress.get(key)
    if (entry) entry.names.push(fullName(a))
    else byAddress.set(key, { address: raw, names: [fullName(a)] })
  }
  return Array.from(byAddress.values())
}

/** How many letterboxes the selection covers. */
export const householdCount = (selected: Selected): number => householdGroups(selected).length

export function buildLabels(selected: Selected, roster: Agent[]): Uint8Array {
  return buildLabelsDocx(labelsFor(selected, roster))
}

/** For callers that already have the lines and want the count to match. */
export const buildLabelsDocxFrom = (labels: string[][]): Uint8Array => buildLabelsDocx(labels)

/** yyyy-mm-dd, for a filename that sorts by date in a downloads folder. */
export function stamp(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
}

export function download(bytes: Uint8Array, filename: string, mime: string): void {
  // Blob accepts a typed array directly and honours its offset and length, so
  // there is no need to copy a megabyte to hand it over. The cast is only to
  // satisfy TypeScript's newer BlobPart type, which insists the backing buffer
  // is an ArrayBuffer and not a SharedArrayBuffer; ours always is.
  const url = URL.createObjectURL(new Blob([bytes as unknown as BlobPart], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoked on the next tick rather than immediately — Safari has been known to
  // cancel a download whose URL disappears in the same frame.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
